/**
 * Shared constants and JSDoc contracts for the rebuild system.
 * This module is intentionally runtime-light: constants only, no logic.
 */

window.JRS = window.JRS || {};

/** @enum {string} */
window.JRS.APPLICATION_METHODS = Object.freeze({
  EMBROIDERY: 'Embroidery',
  PRINT: 'Print'
});

/** @enum {string} */
window.JRS.LOGO_TYPES = Object.freeze({
  UPLOAD: 'upload',
  EXISTING: 'existing',
  TEXT: 'text'
});

/**
 * Display-facing logo type strings used in Shopify line item properties.
 * These are what fulfillment reads from "Logo Type N" properties.
 * @enum {string}
 */
window.JRS.LOGO_TYPE_DISPLAY = Object.freeze({
  UPLOAD: 'Upload Your Logo',
  EXISTING: 'Existing Logo',
  TEXT: 'Text Logo'
});

/** @enum {string} */
window.JRS.PDP_TYPES = Object.freeze({
  STANDARD: 'standard',
  BUNDLE: 'bundle'
});

/** @enum {string} */
window.JRS.PRODUCT_SELECTOR_STRATEGIES = Object.freeze({
  SINGLE_VARIANT: 'singleVariant',
  MULTI_STEP: 'multiStep',
  BUNDLE_CATEGORY: 'bundleCategory'
});

/**
 * Root configuration object for a complete PDP selection session.
 * @typedef {Object} ProductConfiguration
 * @property {'standard'|'bundle'} type - Discriminator for payload shape and validation
 * @property {string} instanceId - UUID v4 generated per add-to-cart attempt (idempotency key)
 * @property {string} timestamp - ISO 8601 timestamp of configuration completion
 * @property {CategoryConfiguration[]} categories - For standard: single-element array. For bundle: one per category.
 * @property {CustomizationConfiguration|null} customization - Null if plain product (no logo)
 * @property {string} [bundleMetaobjectId] - Bundle only: metaobject ID for order reconstruction
 * @property {string} [bundleProductHandle] - Bundle only: the bundle product's Shopify handle
 * @property {number} [bundleProductVariantId] - Bundle only: the bundle product's Shopify variant ID — REQUIRED on bundles per Doc 2 §13.13. Populated by the bundle PDP Liquid template from `product.selected_or_first_available_variant.id` at popup mount time. Read by the cart-payload-builder for the parent line item id. Missing on a bundle configuration triggers a validator warning and a fallback to the first sub-product's variant id (legacy behaviour, preserves Phase 1 resumption).
 */

/**
 * Category-level grouping of selected products.
 * @typedef {Object} CategoryConfiguration
 * @property {string} name - Display name: "Polos", "Hoodies", or product title for standard
 * @property {number} index - 0-based index controlling numbered property order on parent
 * @property {SelectedProduct[]} products - Products selected within this category
 */

/**
 * One selected product within a category.
 * @typedef {Object} SelectedProduct
 * @property {number} productId - Shopify product ID
 * @property {string} productHandle - Shopify product handle (URL slug)
 * @property {string} productTitle - Product display title
 * @property {number} variantId - Primary variant ID (first selected, or single-variant)
 * @property {string} colour - Selected colour name
 * @property {string|null} imageUrl - Product image URL or null
 * @property {SizeQuantity[]} sizes - Size-quantity pairs (always at least one element)
 */

/**
 * Quantity selection for one size.
 * @typedef {Object} SizeQuantity
 * @property {string} size - Size label: "S", "M", "L", "XL", "2XL", etc.
 * @property {number} quantity - Customer-entered quantity for this size (always > 0)
 * @property {number} variantId - Resolved Shopify variant ID for this colour+size combination
 * @property {string|null} sku - Shopify SKU or null
 */

/**
 * Collection of all position/logo assignments.
 * @typedef {Object} CustomizationConfiguration
 * @property {PositionAssignment[]} positions - Ordered array; index determines numbered property N (1-based in output)
 */

/**
 * A single customized garment position.
 * @typedef {Object} PositionAssignment
 * @property {string} id - UUID v4, unique per position instance
 * @property {string} positionName - Human-readable: "Left Breast", "Large Back", "Cap Front"
 * @property {number} positionVariantId - Shopify variant ID of the position product for this application method
 * @property {'Embroidery'|'Print'} applicationMethod - Application method
 * @property {number} categoryIndex - Index into ProductConfiguration.categories[]
 * @property {string|null} sourceLogoGroupId - Links positions created by "same for all" toggle; ignored by CartPayloadBuilder
 * @property {LogoConfiguration} logo - The logo assigned to this position
 */

/**
 * Logo assignment details for a position.
 * @typedef {Object} LogoConfiguration
 * @property {'upload'|'existing'|'text'} type - Logo source type
 * @property {string} logoId - UUID v4 generated at selection time (never changes)
 * @property {string|null} url - For upload/existing: validated HTTPS URL. Null for text logos.
 * @property {string|null} filename - For upload: original filename. Null otherwise.
 * @property {TextLogoData|null} text - For text logos only. Null for upload/existing.
 * @property {string} note - Customer notes (empty string if none)
 */

/**
 * Text logo content and style.
 * @typedef {Object} TextLogoData
 * @property {[string, string, string, string]} lines - Always 4 elements; empty string for unused lines.
 * @property {string} colour - Text colour (hex string, e.g., "#1A1A6C")
 * @property {string} font - Font name (e.g., "Arial Bold", "Standard")
 */

/**
 * Payload sent to Shopify /cart/add.js.
 * @typedef {Object} CartPayload
 * @property {CartLineItem[]} items - Ordered: parent(s) first, then children
 */

/**
 * Single cart line item used in CartPayload.
 * @typedef {Object} CartLineItem
 * @property {number} id - Shopify variant ID
 * @property {number} quantity - Quantity to add
 * @property {LineItemProperties} properties - Shopify line item properties
 */

/**
 * Known line item property keys used by the system.
 * Not enforced at runtime — Shopify accepts any string key.
 * This typedef serves as a documentation reference for all property keys
 * written by CartPayloadBuilder and read by CartValidator, CartPage, and fulfillment.
 *
 * BUNDLE PARENT properties:
 *   _bundle                - URL to bundle page for reconstruction
 *   _bundle_raw            - JSON string: full bundle definition (categories, products, sizes, positions)
 *   _bundle_instance_id    - UUID grouping all items from one add-to-cart operation
 *   _logoproduct{N}        - Category name for position N (e.g., "Polos")
 *   _logoposition{N}       - Position name for position N (e.g., "Left Breast")
 *   _logoVariant{N}        - Position variant Shopify ID for position N
 *   Position {N}           - Position name (synced with _logoposition{N})
 *   Application Method {N} - "Embroidery" or "Print"
 *   Logo Type {N}          - "Upload Your Logo" | "Existing Logo" | "Text Logo"
 *   Logo Id {N}            - UUID of the logo
 *   Logo {N}               - Logo image URL (upload/existing only)
 *   Logo Text {N}          - Text line 1 (text logo only)
 *   Logo Text 2 {N}        - Text line 2 (text logo only)
 *   Logo Text 3 {N}        - Text line 3 (text logo only)
 *   Logo Text 4 {N}        - Text line 4 (text logo only)
 *   Logo Color {N}         - Text colour (text logo only)
 *   Logo Font {N}          - Text font (text logo only)
 *   Note {N}               - Customer notes for position N
 *   _system_version        - "v2" (identifies items created by new system)
 *
 * STANDARD PARENT properties:
 *   _customization_data    - JSON string: { type, positions[], logoIds[], parentVariantId, customizationCount, timestamp }
 *   _has_customization     - "true" (string, not boolean)
 *   _customization_count   - Number of positions (number type)
 *   _all_logo_ids          - Comma-separated logo UUIDs
 *   Logo Id                - Primary logo ID (first position)
 *   _logoVariant{N}        - Position variant Shopify ID for position N
 *   Position {N}           - Same numbered properties as bundle, but without _logoproduct{N} and _logoposition{N}
 *   Application Method {N}
 *   Logo Type {N}
 *   Logo Id {N}
 *   Logo {N}
 *   Logo Text {N}, Logo Text 2 {N}, Logo Text 3 {N}, Logo Text 4 {N}
 *   Logo Color {N}, Logo Font {N}
 *   Note {N}
 *   _system_version        - "v2"
 *
 * CHILD properties (both bundle and standard):
 *   _ParentVariant         - Parent line item's variant_id (number)
 *   _lineItemKey           - Dedup key: "{posVariantId}_{parentVariantId}_{logoId}_{Position_Name}" (always 4-segment)
 *   _qty                   - Quantity (number, matches parent)
 *   _time                  - Timestamp (number, ms since epoch)
 *   _bundle_instance_id    - Bundle children only; same UUID as parent
 *   Position               - Position name
 *   Application Method     - "Embroidery" or "Print"
 *   Logo Id                - Logo UUID
 *   Logo Type              - "Upload Your Logo" | "Existing Logo" | "Text Logo"
 *   Logo                   - Logo image URL (upload/existing only)
 *   Logo Filename          - Original filename (upload only)
 *   _logoVariant           - Position variant Shopify ID (string)
 *   Logo Text              - Text line 1 (text logo only)
 *   Logo Text 2            - Text line 2
 *   Logo Text 3            - Text line 3
 *   Logo Text 4            - Text line 4
 *   Logo Color             - Text colour
 *   Logo Font              - Text font
 *   Note                   - Customer notes
 * @typedef {Object<string, string|number>} LineItemProperties
 */

/**
 * Shopify GET /cart.js response (fields consumed by this system).
 * @typedef {Object} ShopifyCartResponse
 * @property {ShopifyCartItem[]} items - All line items in cart
 * @property {number} item_count - Total item count
 * @property {number} total_price - Total price in pence (GBP subunit)
 * @property {string} note - Cart note
 * @property {Object<string, string>} attributes - Cart-level attributes
 */

/**
 * Shopify cart item as returned by /cart.js.
 * @typedef {Object} ShopifyCartItem
 * @property {number} id - Line item ID
 * @property {string} key - Unique cart line key (can change after property updates)
 * @property {number} variant_id - Shopify variant ID
 * @property {number} quantity - Current quantity
 * @property {number} price - Unit price in pence
 * @property {number} line_price - price × quantity
 * @property {string} title - Product title
 * @property {string} variant_title - Variant title (e.g., "Navy / M")
 * @property {string} image - Image URL
 * @property {string} url - Product URL
 * @property {Object<string, any>} properties - Custom line item properties
 * @property {number} product_id - Shopify product ID
 * @property {string} product_type - Shopify product type
 * @property {string} sku - SKU
 */

/**
 * Cart validation summary.
 * @typedef {Object} ValidationResult
 * @property {boolean} valid - True if no critical issues found
 * @property {ValidationIssue[]} issues - All issues found (empty if valid)
 * @property {number} issuesFixed - Count of issues auto-repaired
 */

/**
 * One validation issue.
 * @typedef {Object} ValidationIssue
 * @property {'orphan'|'missing'|'quantityMismatch'|'propertyLost'} type - Issue category
 * @property {number} parentVariantId - Affected parent's variant ID
 * @property {string} details - Human-readable description
 * @property {boolean} fixed - Whether the issue was auto-repaired
 */

/**
 * Result from LogoService.uploadFile().
 * @typedef {Object} LogoUploadResult
 * @property {string} logoId - UUID v4 assigned to this logo
 * @property {string} url - HTTPS URL of uploaded file
 * @property {string} filename - Original filename (sanitized)
 */

/**
 * Event payload for product:colourSelected.
 * @typedef {Object} ProductColourSelectedEvent
 * @property {number} categoryIndex - Category index where colour changed
 * @property {string} colour - Selected colour value
 * @property {string} productHandle - Product handle
 */

/**
 * Event payload for product:sizesChanged.
 * @typedef {Object} ProductSizesChangedEvent
 * @property {number} categoryIndex - Category index where sizes changed
 * @property {SizeQuantity[]} sizes - Selected sizes for the category
 * @property {number} totalQuantity - Total quantity in this category
 */

/**
 * Event payload for bundle:categoryCompleted.
 * @typedef {Object} BundleCategoryCompletedEvent
 * @property {number} categoryIndex - Completed category index
 * @property {string} categoryName - Completed category name
 */

/**
 * Event payload for bundle:allCategoriesCompleted.
 * @typedef {Object} BundleAllCategoriesCompletedEvent
 * @property {number} totalQuantity - Total quantity across all categories
 * @property {number} totalCategories - Number of completed categories
 */

/**
 * Event payload for pdp:stepChanged.
 * @typedef {Object} PdpStepChangedEvent
 * @property {number} step - Current step number
 * @property {number} maxSteps - Maximum steps in flow
 */

/**
 * Event payload for customization:complete.
 * @typedef {Object} CustomizationCompleteEvent
 * @property {string} instanceId - Configuration instance ID that completed
 */

/**
 * Event payload for customizer:stepCompleted.
 * @typedef {Object} CustomizerStepCompletedEvent
 * @property {string} stepName - Logical step identifier
 * @property {string} [positionName] - Optional position context
 * @property {string} [logoType] - Optional logo type context
 */

/**
 * Event payload for customizer:logoUploaded.
 * @typedef {Object} CustomizerLogoUploadedEvent
 * @property {string} logoId - Uploaded logo UUID
 * @property {string} positionName - Position receiving uploaded logo
 * @property {number} fileSize - Uploaded file size in bytes
 */

/**
 * Event payload for cart:updated.
 * @typedef {Object} CartUpdatedEvent
 * @property {ShopifyCartResponse} cart - Latest cart response
 */

/**
 * Event payload for cart:addSucceeded.
 * @typedef {Object} CartAddSucceededEvent
 * @property {CartLineItem[]} items - Items submitted in successful add
 * @property {string} instanceId - Configuration instance ID
 */

/**
 * Event payload for cart:addFailed.
 * @typedef {Object} CartAddFailedEvent
 * @property {string} error - Error message
 * @property {CartPayload} payload - Payload that failed
 * @property {string} instanceId - Configuration instance ID
 */

/**
 * Event payload for cart:editRequested.
 * @typedef {Object} CartEditRequestedEvent
 * @property {string} parentKey - Parent Shopify cart key
 * @property {string[]} childKeys - Child Shopify cart keys
 * @property {string} productUrl - Source product URL
 * @property {('standard'|'bundle')} productType - Product type discriminator
 */

/**
 * Event payload for cart:quantityChanged.
 * @typedef {Object} CartQuantityChangedEvent
 * @property {string} lineKey - Target line key
 * @property {number} oldQuantity - Quantity before the change
 * @property {number} newQuantity - Updated quantity
 * @property {number} variantId - Variant ID for analytics and reconciliation
 */

/**
 * Event payload for cart:clearClicked (Clear Cart button, cart page).
 * @typedef {Object} CartClearClickedEvent
 * @property {number} itemCount - cart.item_count at click time
 * @property {number} totalPrice - cart.total_price (minor units) at click time
 */

/**
 * Event payload for cart:checkoutClicked (checkout button, cart page).
 * @typedef {Object} CartCheckoutClickedEvent
 * @property {number} itemCount - cart.item_count at click time
 * @property {number} totalPrice - cart.total_price (minor units) at click time
 */

/**
 * Event payload for cart:itemRemoved.
 * @typedef {Object} CartItemRemovedEvent
 * @property {string} lineKey - Removed line key
 * @property {number} variantId - Removed variant ID
 * @property {boolean} wasCustomized - Whether removed item had customization
 */

/**
 * Event payload for validation:issueFound.
 * @typedef {Object} ValidationIssueFoundEvent
 * @property {'orphan'|'missing'|'quantityMismatch'|'propertyLost'} type - Issue category
 * @property {number} parentVariantId - Affected parent variant ID
 * @property {string} details - Human-readable issue details
 */

/**
 * Event payload for validation:completed.
 * @typedef {Object} ValidationCompletedEvent
 * @property {number} issuesFound - Total issues found
 * @property {number} issuesFixed - Total issues auto-fixed
 * @property {number} duration - Validation duration in milliseconds
 */

/**
 * Example objects for test fixtures and quick reference.
 *
 * @example Standard customized product
 * const EXAMPLE_STANDARD_CONFIG = {
 *   type: 'standard',
 *   instanceId: 'a1b2c3d4-e5f6-4a7b-8c9d-0e1f2a3b4c5d',
 *   timestamp: '2026-03-26T14:30:00.000Z',
 *   categories: [{
 *     name: 'Pro RTX Polo Shirt',
 *     index: 0,
 *     products: [{
 *       productId: 7891234567890,
 *       productHandle: 'pro-rtx-polo-shirt',
 *       productTitle: 'Pro RTX Polo Shirt',
 *       variantId: 43625638002931,
 *       colour: 'Navy',
 *       imageUrl: 'https://cdn.shopify.com/s/files/1/0123/4567/8901/products/polo-navy.jpg',
 *       sizes: [
 *         { size: 'M', quantity: 10, variantId: 43625638002931, sku: 'RTX-POLO-NAV-M' }
 *       ]
 *     }]
 *   }],
 *   customization: {
 *     positions: [
 *       {
 *         id: 'pos-1111-2222-3333-4444',
 *         positionName: 'Left Breast',
 *         positionVariantId: 44333841875101,
 *         applicationMethod: 'Embroidery',
 *         categoryIndex: 0,
 *         sourceLogoGroupId: null,
 *         logo: {
 *           type: 'existing',
 *           logoId: 'logo-aaaa-bbbb-cccc-dddd',
 *           url: 'https://cdn.shopify.com/s/files/1/0123/4567/8901/files/acme-corp-logo.png',
 *           filename: null,
 *           text: null,
 *           note: ''
 *         }
 *       },
 *       {
 *         id: 'pos-5555-6666-7777-8888',
 *         positionName: 'Large Back',
 *         positionVariantId: 44737893859571,
 *         applicationMethod: 'Print',
 *         categoryIndex: 0,
 *         sourceLogoGroupId: null,
 *         logo: {
 *           type: 'text',
 *           logoId: 'logo-eeee-ffff-0000-1111',
 *           url: null,
 *           filename: null,
 *           text: {
 *             lines: ['JRS INDUSTRIAL', 'Est. 2015', '', ''],
 *             colour: '#1A1A6C',
 *             font: 'Arial Bold'
 *           },
 *           note: ''
 *         }
 *       }
 *     ]
 *   }
 * };
 *
 * @example 3-category bundle
 * const EXAMPLE_BUNDLE_CONFIG = {
 *   type: 'bundle',
 *   instanceId: 'bnd-1234-5678-9abc-def0',
 *   timestamp: '2026-03-26T15:00:00.000Z',
 *   categories: [
 *     {
 *       name: 'Polos',
 *       index: 0,
 *       products: [{
 *         productId: 7891234567890,
 *         productHandle: 'pro-rtx-polo-shirt',
 *         productTitle: 'Pro RTX Polo Shirt',
 *         variantId: 43625638002931,
 *         colour: 'Navy',
 *         imageUrl: 'https://cdn.shopify.com/s/files/1/polo.jpg',
 *         sizes: [
 *           { size: 'S', quantity: 4, variantId: 43625638002900, sku: 'RTX-POLO-NAV-S' },
 *           { size: 'M', quantity: 6, variantId: 43625638002931, sku: 'RTX-POLO-NAV-M' },
 *           { size: 'L', quantity: 4, variantId: 43625638002962, sku: 'RTX-POLO-NAV-L' },
 *           { size: 'XL', quantity: 2, variantId: 43625638002993, sku: 'RTX-POLO-NAV-XL' }
 *         ]
 *       }]
 *     },
 *     {
 *       name: 'Hoodies',
 *       index: 1,
 *       products: [{
 *         productId: 7891234567891,
 *         productHandle: 'pro-rtx-hoodie',
 *         productTitle: 'Pro RTX Hoodie',
 *         variantId: 43625638035699,
 *         colour: 'Black',
 *         imageUrl: 'https://cdn.shopify.com/s/files/1/hoodie.jpg',
 *         sizes: [
 *           { size: 'M', quantity: 3, variantId: 43625638035699, sku: 'RTX-HOOD-BLK-M' },
 *           { size: 'L', quantity: 3, variantId: 43625638035730, sku: 'RTX-HOOD-BLK-L' }
 *         ]
 *       }]
 *     },
 *     {
 *       name: 'Jackets',
 *       index: 2,
 *       products: [{
 *         productId: 7891234567892,
 *         productHandle: 'result-softshell-jacket',
 *         productTitle: 'Result Softshell Jacket',
 *         variantId: 43625638068467,
 *         colour: 'Navy',
 *         imageUrl: 'https://cdn.shopify.com/s/files/1/jacket.jpg',
 *         sizes: [
 *           { size: 'L', quantity: 2, variantId: 43625638068467, sku: 'RES-SOFT-NAV-L' },
 *           { size: 'XL', quantity: 2, variantId: 43625638068498, sku: 'RES-SOFT-NAV-XL' }
 *         ]
 *       }]
 *     }
 *   ],
 *   customization: {
 *     positions: [
 *       {
 *         id: 'bpos-0001-aaaa-bbbb-cccc',
 *         positionName: 'Left Breast',
 *         positionVariantId: 44333841875101,
 *         applicationMethod: 'Embroidery',
 *         categoryIndex: 0,
 *         sourceLogoGroupId: 'shared-logo-group-1',
 *         logo: {
 *           type: 'existing',
 *           logoId: 'logo-shared-aaaa-bbbb',
 *           url: 'https://cdn.shopify.com/s/files/1/0123/4567/8901/files/acme-corp-logo.png',
 *           filename: null,
 *           text: null,
 *           note: ''
 *         }
 *       },
 *       {
 *         id: 'bpos-0002-dddd-eeee-ffff',
 *         positionName: 'Large Back',
 *         positionVariantId: 44737893859571,
 *         applicationMethod: 'Print',
 *         categoryIndex: 1,
 *         sourceLogoGroupId: null,
 *         logo: {
 *           type: 'upload',
 *           logoId: 'logo-upload-cccc-dddd',
 *           url: 'https://jrsindustrial-files.com/uploads/logo_1711450800_company-logo.png',
 *           filename: 'company-logo.png',
 *           text: null,
 *           note: 'Please make logo 30cm wide'
 *         }
 *       },
 *       {
 *         id: 'bpos-0003-0000-1111-2222',
 *         positionName: 'Left Breast',
 *         positionVariantId: 44333841875101,
 *         applicationMethod: 'Embroidery',
 *         categoryIndex: 2,
 *         sourceLogoGroupId: 'shared-logo-group-1',
 *         logo: {
 *           type: 'existing',
 *           logoId: 'logo-shared-aaaa-bbbb',
 *           url: 'https://cdn.shopify.com/s/files/1/0123/4567/8901/files/acme-corp-logo.png',
 *           filename: null,
 *           text: null,
 *           note: ''
 *         }
 *       }
 *     ]
 *   },
 *   bundleMetaobjectId: 'gid://shopify/Metaobject/12345678',
 *   bundleProductHandle: 'starter-workwear-bundle'
 * };
 *
 * // NOTE: Positions at index 0 (Polos, Left Breast) and index 2 (Jackets, Left Breast)
 * // share sourceLogoGroupId 'shared-logo-group-1' and the same logoId 'logo-shared-aaaa-bbbb'.
 * // This is the "same for all" pattern expanded into separate PositionAssignment objects.
 * // CartPayloadBuilder treats these as independent positions and only uses positions[] order.
 */
